import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { GUIDES } from "@/lib/data";
import { api, usePageMeta } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { Reveal } from "@/components/Reveal";

export function GuidesPage() {
  usePageMeta("Guías y consejos prácticos · solsdiarias360", "Artículos originales para organizar tu casa, tu cocina, tu escritorio, tu coche y tus viajes.");

  return (
    <div className="App">
      <Header />
      <main className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Reveal>
            <p className="text-amber-600 font-mono-s360 text-xs uppercase tracking-[0.22em] mb-2">Contenido editorial</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Guías y consejos prácticos</h1>
            <p className="text-slate-600 text-sm sm:text-base mt-3 max-w-2xl leading-relaxed">
              Artículos originales escritos para resolver problemas reales de organización y día a día.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
            {GUIDES.map((g, i) => (
              <Reveal key={g.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/guia/${g.slug}`}
                  data-testid={`guide-card-${g.slug}`}
                  className="group block bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform] duration-300 h-full"
                >
                  <div className="h-44 overflow-hidden">
                    <img src={g.image} alt={g.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-5">
                    <p className="text-[11px] font-mono-s360 text-amber-600 uppercase tracking-widest mb-2">{g.minutes} min de lectura</p>
                    <h2 className="font-display font-bold text-slate-900 text-lg leading-snug group-hover:text-amber-600 transition-colors">{g.title}</h2>
                    <p className="text-slate-600 text-sm mt-2 leading-relaxed">{g.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

export function GuidePage() {
  const { slug } = useParams();
  const guide = GUIDES.find((g) => g.slug === slug);
  const others = GUIDES.filter((g) => g.slug !== slug).slice(0, 2);
  const [products, setProducts] = useState([]);

  usePageMeta(
    guide ? `${guide.title} · solsdiarias360` : "Guía · solsdiarias360",
    guide?.excerpt
  );

  useEffect(() => {
    setProducts([]);
    if (!guide?.products?.length) return;
    Promise.all(
      guide.products.map((id) => api.get(`/products/${id}`).then((r) => r.data).catch(() => null))
    ).then((list) => setProducts(list.filter(Boolean)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!guide) {
    return (
      <div className="App">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-display text-2xl font-bold text-slate-900">Guía no encontrada</h1>
          <Link to="/guias" className="text-amber-600 font-semibold text-sm mt-4 inline-block">Ver todas las guías</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <main className="bg-white min-h-screen">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="guide-article">
          <Link to="/guias" className="text-amber-600 font-semibold text-sm flex items-center gap-1.5 mb-8" data-testid="back-to-guides">
            <ArrowLeft size={15} /> Todas las guías
          </Link>
          <Reveal>
            <p className="text-amber-600 font-mono-s360 text-xs uppercase tracking-[0.22em] mb-3">{guide.minutes} min de lectura</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">{guide.title}</h1>
            <p className="text-slate-600 text-base sm:text-lg mt-4 leading-relaxed">{guide.intro}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <img src={guide.image} alt={guide.title} className="w-full h-64 sm:h-80 object-cover rounded-2xl mt-8" />
          </Reveal>

          {guide.sections.map((s, i) => (
            <Reveal key={i} delay={0.05}>
              <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-10">{s.heading}</h2>
              <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">{s.body}</p>
            </Reveal>
          ))}

          <Reveal>
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mt-10">
              <h2 className="font-display text-lg font-bold text-slate-900 mb-4">Tres ideas para llevarte</h2>
              <ul className="space-y-3">
                {guide.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <CheckCircle2 size={16} className="text-amber-500 shrink-0 mt-0.5" /> {tip}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          {products.length > 0 && (
            <Reveal>
              <div className="mt-12" data-testid="guide-products">
                <h2 className="font-display text-xl font-bold text-slate-900 tracking-tight">Productos mencionados en esta guía</h2>
                <p className="text-slate-500 text-sm mt-1.5">Selección de nuestro catálogo relacionada con este consejo.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
                  {products.map((p) => (
                    <Link
                      key={p.id}
                      to={`/producto/${p.id}`}
                      data-testid={`guide-product-${p.id}`}
                      className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300"
                    >
                      <div className="h-28 bg-slate-50 overflow-hidden">
                        <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-3">
                        <p className="text-xs font-semibold text-slate-900 leading-snug line-clamp-2 min-h-8">{p.name}</p>
                        <p className="text-amber-600 text-xs font-bold mt-2 flex items-center gap-1">Ver producto <ArrowRight size={12} strokeWidth={2.5} /></p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {guide.category && (
            <Reveal>
              <Link
                to={`/categoria/${guide.category}`}
                data-testid="guide-category-cta"
                className="mt-8 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 py-3.5 rounded-lg transition-colors"
              >
                Ver productos relacionados <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
            </Reveal>
          )}

          <div className="border-t border-slate-200 mt-12 pt-8">
            <h2 className="font-display text-lg font-bold text-slate-900 mb-5">Sigue leyendo</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {others.map((g) => (
                <Link key={g.slug} to={`/guia/${g.slug}`} data-testid={`related-guide-${g.slug}`} className="group border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                  <h3 className="font-display font-semibold text-slate-900 text-sm leading-snug group-hover:text-amber-600 transition-colors">{g.title}</h3>
                  <p className="text-slate-500 text-xs mt-2 line-clamp-2">{g.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
